import type { DateTime } from "luxon";
import type { AnalyticValues } from "../../../../contexts/analytics/hooks";
import { type NurseFullRole, RequestSource } from "../../../../utils/models";
import type { useCheckOverlappingShiftsAndPatch } from "../../hooks/useHandleCheckOverlappingShifts";

/** How a post attempt ended — the analytics outcome, and whether the sheet closes. */
export enum PostOutcome {
	Posted = "posted",
	Merged = "merged",
	/** The overlap prompt's "Keep": nothing was written, the sheet stays for another try. */
	Kept = "kept",
	Failed = "failed",
}

/** What one post writes: the form's fields as the classic post screen sends them. */
export type GapsPostShiftWrite = {
	neededNurses: number;
	shiftStart: DateTime;
	shiftEnd: DateTime;
	selectedRoles: NurseFullRole[];
	/** The job posted for, or `undefined` for a post by role group. */
	jobId: string | undefined;
	bonus: number;
	chargeOnly: boolean;
	/** The unit posted to, or `undefined` for any unit. */
	location: string | undefined;
};

type CheckForOverlaps = ReturnType<typeof useCheckOverlappingShiftsAndPatch>["checkForOverlapsAndMergeIfUserApproved"];

/**
 * Runs the post through `useCheckOverlappingShiftsAndPatch` — the overlap merge prompt, the post and the requests refresh
 * are all that hook's, unchanged — and resolves how it ended.
 *
 * Each branch of the wrapped hook reports through a different callback; one of them names the outcome. No callback at
 * all means the overlap prompt was answered "Merge" and the needed-nurses write then failed: the hook has already shown
 * that error and never calls back, so silence reads as a failure.
 */
export const runPostWrite = async (checkForOverlaps: CheckForOverlaps, write: GapsPostShiftWrite): Promise<PostOutcome> => {
	// An object, not a `let`: the callbacks write it, and TypeScript's narrowing cannot see a write made inside a closure.
	const result: { outcome: PostOutcome | null } = { outcome: null };
	await checkForOverlaps({
		...write,
		panel: "gaps-sheet",
		requestSource: RequestSource.PostShift,
		actionsWhenMergeNotNeeded: {
			onSuccess: () => {
				result.outcome = PostOutcome.Posted;
			},
			onError: () => {
				result.outcome = PostOutcome.Failed;
			},
		},
		actionAfterMerge: () => {
			result.outcome = PostOutcome.Merged;
		},
		actionIfMergeCanceled: () => {
			result.outcome = PostOutcome.Kept;
		},
	});
	// DEFAULT: "failed" is the one reading of silence — every path that wrote something calls back.
	return result.outcome ?? PostOutcome.Failed;
};

/** The `gaps_post_shift_*` events' properties for one post. */
export const postShiftAnalytics = ({
	departmentId,
	isPrefilled,
	groupKey,
	postsByJob,
	jobId,
	roleGroups,
	write,
}: {
	departmentId: string;
	isPrefilled: boolean;
	/** `null` for an unscoped post (the bar's Post): the event says there was no group; it stands in for nothing. */
	groupKey: string | null;
	postsByJob: boolean;
	jobId: string;
	roleGroups: string[];
	write: GapsPostShiftWrite;
}): AnalyticValues => ({
	department_id: departmentId,
	prefilled: isPrefilled,
	group: groupKey,
	needed_nurses: write.neededNurses,
	job_id: postsByJob ? jobId : null,
	role_groups: postsByJob ? null : roleGroups.join(","),
	has_location: write.location !== undefined,
	bonus_amount: write.bonus,
	charge_only: write.chargeOnly,
});
